import { apiRoutes } from '@/constants/api-routes';
import { apiRequest, type QueryParams } from './http';
import type {
  BookAccountMetadataView,
  BookLabelsView,
  BookView,
  PaginatedBooks,
  SimilarBooksResult,
} from '@/types/api';

/**
 * Query params accepted by `GET /books`. Each label-type key takes an array
 * of aliases; the HTTP client serializes each entry as a repeated query
 * param (`?genre=Fantasy&genre=Mystery`) which is what the server's
 * `toStringArray` transform expects.
 *
 * `randomize` flips the RPC into shuffle mode — used for the async library
 * carousels that want a fresh sample on every visit.
 */
export interface ListBooksFilters {
  genre?: string[];
  topic?: string[];
  reading_level?: string[];
  interest_level?: string[];
  historical_event?: string[];
  time_period?: string[];
  book_series?: string[];
  /**
   * Reader-driven hard filter applied as INTERSECT on top of the category
   * label filter. Used by the Library carousels to scope every strip to the
   * selected reader's reading level. Omitted/empty = no constraint.
   */
  required_reading_level?: string[];
  limit?: number;
  offset?: number;
  randomize?: boolean;
  /**
   * Free-text title search. Currently honoured only by the admin list
   * endpoint — the public list silently ignores it. Surfaced on the
   * shared filter shape so the admin dashboard's search-as-you-type
   * box can route through `adminBooksService.list()` without a parallel
   * type.
   */
  q?: string;
}

export interface SimilarBooksFilters {
  limit?: number;
  offset?: number;
}

/**
 * Converts the typed filter object into the loose `QueryParams` shape the
 * HTTP client expects. The shape is functionally identical — this wrapper
 * just narrows the type so call sites don't have to cast.
 */
function toQueryParams(filters: ListBooksFilters | SimilarBooksFilters): QueryParams {
  const query: QueryParams = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    query[key] = value as QueryParams[string];
  }
  return query;
}

/**
 * Body for `POST /books` — the admin dashboard's "Add Book" dialog. The
 * server auto-assigns `pending` status and leaves every other field blank;
 * the editor fills them in afterwards.
 */
export interface CreateBookPayload {
  title: string;
}

/**
 * Body for `PATCH /books/:id`. Mirrors `UpdateBookDto` on the server —
 * every field is optional, and `author` / `description` accept `null` so
 * the admin can clear a previously-set value. `status` is constrained to
 * the two live/pending values the DB CHECK constraint allows.
 */
export interface UpdateBookPayload {
  title?: string;
  author?: string | null;
  description?: string | null;
  status?: 'pending' | 'live';
  /** Reading-tracker metadata. `null` clears the column. */
  pages?: number | null;
  chapters?: number | null;
  /** Amazon affiliate URL. `null` (or empty string) clears the column. */
  amazonAffiliateUrl?: string | null;
}

export const booksService = {
  /**
   * Lists live books. When no filters are supplied the server returns the
   * first 20 books ordered by `created_at DESC`; pass `randomize: true` to
   * get a shuffled sample instead.
   */
  list(filters: ListBooksFilters = {}) {
    return apiRequest<PaginatedBooks>(apiRoutes.books.list, {
      query: toQueryParams(filters),
    });
  },
  getById(id: string) {
    return apiRequest<BookView>(apiRoutes.books.one(id));
  },
  /**
   * Admin-only. Creates a new book with just the title — the editor flow
   * fills in the rest. The server returns the full `BookView` so the caller
   * can navigate to `/admin/books/:id` without a second round-trip.
   */
  create(payload: CreateBookPayload) {
    return apiRequest<BookView>(apiRoutes.books.list, {
      method: 'POST',
      body: payload,
    });
  },
  /**
   * Admin-only. Partially updates a book's metadata / status. Returns the
   * full refreshed `BookView` so the caller can overwrite local state
   * without a follow-up GET.
   */
  update(id: string, payload: UpdateBookPayload) {
    return apiRequest<BookView>(apiRoutes.books.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },
  /**
   * Admin-only. Uploads a new cover image and returns the refreshed book.
   * The server reads the first multipart file from the request (see
   * `BooksController.uploadCover`), so we forward a `FormData` body with
   * exactly one `file` field.
   */
  uploadCover(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return apiRequest<BookView>(apiRoutes.books.cover(id), {
      method: 'POST',
      body: formData,
      raw: true, // let fetch set the multipart boundary header for us
    });
  },
  /**
   * Admin-only. Removes the stored cover and clears `book_cover` on the
   * row. Returns the refreshed book with `bookCover: null`.
   */
  deleteCover(id: string) {
    return apiRequest<BookView>(apiRoutes.books.cover(id), {
      method: 'DELETE',
    });
  },
  /**
   * Admin-only. Uploads the per-book "vocabulary bookmark" image. Same
   * multipart contract as `uploadCover` — server reads the first file
   * from the request via `req.file()`.
   */
  uploadVocabularyBookmark(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return apiRequest<BookView>(apiRoutes.books.vocabularyBookmark(id), {
      method: 'POST',
      body: formData,
      raw: true, // let fetch set the multipart boundary header for us
    });
  },
  /**
   * Admin-only. Clears `vocabulary_bookmark` and best-effort unlinks the
   * file on disk. Returns the refreshed book with `vocabularyBookmark: null`.
   */
  deleteVocabularyBookmark(id: string) {
    return apiRequest<BookView>(apiRoutes.books.vocabularyBookmark(id), {
      method: 'DELETE',
    });
  },
  /**
   * Admin-only. Synchronously regenerates the book's pgvector embedding
   * via OpenAI and stamps `embedding_generated_at`. Returns the refreshed
   * `BookView` so the editor can show the updated timestamp inline.
   * Expect ~1–2 seconds while OpenAI returns.
   */
  regenerateEmbedding(id: string) {
    return apiRequest<BookView>(apiRoutes.books.embedding(id), {
      method: 'POST',
    });
  },
  /**
   * Admin-only. Fetches the label catalogue attached to a book, grouped
   * by type. Used by the editor's label sidebar to know which aliases
   * are already linked before rendering the multi-selects.
   */
  getLabels(id: string) {
    return apiRequest<BookLabelsView>(apiRoutes.books.labels(id));
  },
  /**
   * Admin-only. Links a label to a book. The server upserts against the
   * `(book_id, label_id)` unique constraint so calling twice is a no-op.
   */
  addLabel(id: string, labelId: string) {
    return apiRequest<BookLabelsView>(apiRoutes.books.label(id, labelId), {
      method: 'PUT',
    });
  },
  /**
   * Admin-only. Unlinks a label from a book. Returns the refreshed
   * `BookLabelsView` so the editor can mirror server state without a
   * separate GET.
   */
  removeLabel(id: string, labelId: string) {
    return apiRequest<BookLabelsView>(apiRoutes.books.label(id, labelId), {
      method: 'DELETE',
    });
  },
  /**
   * Reading-tracker per-account page/chapter overrides. The GET resolves the
   * effective counts (`override ?? catalogue`) plus the raw override + global
   * values so the wizard can prefill, label the family's own value, and offer
   * "reset to catalogue". The catalogue's own counts stay admin-only — these
   * writes only touch `book_account_metadata`, never the shared book row.
   */
  getAccountMetadata(id: string) {
    return apiRequest<BookAccountMetadataView>(apiRoutes.books.accountMetadata(id));
  },
  /**
   * Upserts this account's override of a book's page/chapter counts. Per-field
   * tri-state: omit a field to leave it untouched, send a number to override,
   * send `null` to clear the override (revert to the catalogue value).
   */
  saveAccountMetadata(
    id: string,
    payload: { pages?: number | null; chapters?: number | null },
  ) {
    return apiRequest<BookAccountMetadataView>(apiRoutes.books.accountMetadata(id), {
      method: 'PATCH',
      body: payload,
    });
  },
  /**
   * Cosine-similarity neighbours for a source book. The source book itself
   * is always excluded by the RPC.
   */
  similar(id: string, filters: SimilarBooksFilters = {}) {
    return apiRequest<SimilarBooksResult>(apiRoutes.books.similar(id), {
      query: toQueryParams(filters),
    });
  },
};
